/**
 * DataValueChip — minimal inline rendering of a single get_cell_metric
 * result. The chip itself is intentionally tiny — just the verified-data
 * mark, the value, and the unit, in Batemo orange. Hover or focus
 * reveals a tooltip with the cell name and the human-readable metric
 * label (NEVER the backend column name).
 *
 * Tooltip positioning:
 *   The tooltip used to be `position: absolute` inside the chip's
 *   wrapper. That made it clip whenever an ancestor had
 *   `overflow: hidden / auto` — most painfully against the chat
 *   message-list's left edge. To survive any ancestor clipping we
 *   render the tooltip into a portal on `document.body` with
 *   `position: fixed`, then compute its viewport coordinates from the
 *   chip's bounding rect each time it opens. We clamp horizontally to
 *   the viewport so the tooltip never disappears off either edge.
 *
 * Hover/focus contract:
 *   - tooltip opens on `mouseenter` AND `focus` (keyboard a11y)
 *   - dismisses on `mouseleave`/`blur`
 *   - body uses role="tooltip" + aria-describedby for screen readers
 *   - `title` attribute deliberately omitted — that would race with
 *     our custom tooltip on some browsers
 *
 * @param {object} props
 * @param {object} props.cell — { manufacturer, name, chemistry }
 * @param {object} props.metric — { name, label, unit }
 * @param {string} props.formatted — pre-formatted numeric string
 * @param {string} [props.basis]
 * @param {Array<object>} [props.provenanceTrail] — ordered list of trail steps
 *   the backend recorded for this value (fetched / cohort / aggregate /
 *   input / computed). The tooltip surfaces these so a user can audit
 *   the exact chain that produced the chip's number.
 * @param {object} [props.provenance] — Ion Next only (MarkdownRenderer's opt-in
 *   `chipProvenance`): the artifact's `provenance`. A derived / estimate /
 *   recommendation kind makes the chip label itself (markup, tooltip,
 *   aria-label, check mark; see chipProvenance.js). Absent, or without one of
 *   those kinds, the chip is exactly the customer chip.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from 'preact/hooks'
import { createPortal } from 'preact/compat'
import { chipProvenanceInfo } from './chipProvenance.js'

const TOOLTIP_GAP = 6 // px between chip and tooltip
const TOOLTIP_VIEWPORT_PAD = 8 // px from viewport edge

// Human-readable rendering of a single provenance step. Mirrors the
// backend's step contract — see calculate.py, get_cell_metric.py,
// derive_metric.py, aggregate_metric.py for the shapes.
function renderTrailStep(step, idx) {
  if (!step || typeof step !== 'object') return null
  const kind = step.step
  if (kind === 'fetched') {
    const cellName =
      step.cell && (step.cell.manufacturer || step.cell.name)
        ? `${step.cell.manufacturer || ''} ${step.cell.name || ''}`.trim()
        : null
    return (
      <li key={idx}>
        <strong>{step.source === 'numerator' || step.source === 'denominator'
          ? `Fetched ${step.source}`
          : 'Fetched'}</strong>
        {cellName && <> · {cellName}</>}
        {step.metric_name && <> · <code>{step.metric_name}</code></>}
        {step.expression && (
          <div class="ion-data-value-chip__tooltip-expr">{step.expression}</div>
        )}
      </li>
    )
  }
  if (kind === 'cohort') {
    return (
      <li key={idx}>
        <strong>Cohort</strong>
        {step.cohort_summary && <> · {step.cohort_summary}</>}
        {step.cohort_count != null && <> · {step.cohort_count} cells</>}
      </li>
    )
  }
  if (kind === 'aggregate') {
    return (
      <li key={idx}>
        <strong>Aggregate</strong>
        {step.op && <> · {step.op}</>}
        {step.bucket != null && <> · bucket={String(step.bucket)}</>}
        {step.expression && (
          <div class="ion-data-value-chip__tooltip-expr">{step.expression}</div>
        )}
      </li>
    )
  }
  if (kind === 'input') {
    // Show WHERE the operand came from. A `literal` operand is a number the
    // model typed (a requirement out of the user's question), a `tool_call`
    // operand is a server-fetched, watermarked measurement — and the chip is
    // orange/verified either way, so the difference has to be visible in the
    // trail or the user cannot tell them apart. `label` names the quantity.
    return (
      <li key={idx}>
        <strong>Input{step.side ? ` (${step.side})` : ''}</strong>
        {step.label && <> · {step.label}</>}
        {step.value != null && <> · {step.value}</>}
        {step.unit && <> {step.unit}</>}
        {step.source === 'literal' && <> · stated value (not measured)</>}
        {step.source === 'tool_call' && <> · measured</>}
      </li>
    )
  }
  if (kind === 'computed') {
    return (
      <li key={idx}>
        <strong>Computed</strong>
        {step.op && <> · {step.op}</>}
        {step.expression && (
          <div class="ion-data-value-chip__tooltip-expr">{step.expression}</div>
        )}
      </li>
    )
  }
  // Unknown step kind — render the JSON inline rather than swallowing it,
  // so a missing renderer here doesn't hide the data from the user.
  return (
    <li key={idx}>
      <strong>{kind || 'step'}</strong>
      <div class="ion-data-value-chip__tooltip-expr">
        {JSON.stringify(step)}
      </div>
    </li>
  )
}

export function DataValueChip({ cell, metric, formatted, basis, provenanceTrail, rescuedVia, provenance }) {
  if (!cell || !metric) return null
  const kindInfo = chipProvenanceInfo(provenance)

  // When the backend value-guard had to repair the AI's marker id, it
  // annotates how. Surface that in the tooltip so a rescued chip is
  // distinguishable from a direct citation.
  const rescueNote =
    rescuedVia === 'label'
      ? 'Recovered by matching the metric label (the AI’s reference id was invalid).'
      : rescuedVia === 'ordinal'
        ? 'Recovered by position (best-effort — the AI’s reference id was invalid).'
        : rescuedVia === 'history'
          ? 'Referenced from an earlier message in this conversation.'
          : ''

  const tooltipId = useId()
  const [open, setOpen] = useState(false)
  const chipRef = useRef(null)
  const tooltipRef = useRef(null)
  const [tipStyle, setTipStyle] = useState({ top: 0, left: 0, visibility: 'hidden' })

  const cellLabel = `${cell.manufacturer || ''} ${cell.name || ''}`.trim()
  // metric.label is the human-readable metric name supplied by the backend
  // (e.g. "Specific Energy", "Peak Power"). NEVER show metric.name —
  // that's the LLM-facing id ("specific_energy_grav") and not user-friendly.
  const metricLabel = metric.label || metric.name || ''

  const basisHint =
    basis === 'grav' ? 'gravimetric' : basis === 'vol' ? 'volumetric' : null

  const valueText = `${cellLabel} ${metricLabel} ${formatted} ${metric.unit || ''}`
  const ariaLabel = kindInfo
    ? `${kindInfo.ariaPrefix}: ${valueText}${kindInfo.ariaSuffix ? `. ${kindInfo.ariaSuffix}` : ''}`
    : `Verified data: ${valueText}`

  // Compute tooltip position relative to the chip in viewport space.
  // useLayoutEffect (not useEffect) so the position is set before paint
  // — the tooltip stays hidden via visibility: hidden on the first
  // render, then becomes visible after the rect math runs.
  useLayoutEffect(() => {
    if (!open) return
    const chip = chipRef.current
    const tip = tooltipRef.current
    if (!chip || !tip) return

    const chipRect = chip.getBoundingClientRect()
    const tipRect = tip.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    // Default: above the chip, horizontally centred. Clamp horizontally
    // so the tooltip never spills past the viewport.
    let top = chipRect.top - tipRect.height - TOOLTIP_GAP
    let left = chipRect.left + chipRect.width / 2 - tipRect.width / 2

    // Flip below the chip when there isn't enough room above.
    if (top < TOOLTIP_VIEWPORT_PAD) {
      top = chipRect.bottom + TOOLTIP_GAP
    }
    // Clamp to vertical bounds in the rare case neither side fits.
    if (top + tipRect.height > vh - TOOLTIP_VIEWPORT_PAD) {
      top = Math.max(TOOLTIP_VIEWPORT_PAD, vh - tipRect.height - TOOLTIP_VIEWPORT_PAD)
    }
    // Horizontal clamp.
    const minLeft = TOOLTIP_VIEWPORT_PAD
    const maxLeft = vw - tipRect.width - TOOLTIP_VIEWPORT_PAD
    if (left < minLeft) left = minLeft
    if (left > maxLeft) left = maxLeft

    setTipStyle({ top, left, visibility: 'visible' })
  }, [open])

  // Reposition on scroll / resize while open — otherwise scrolling the
  // chat list leaves the tooltip stranded.
  useEffect(() => {
    if (!open) return undefined
    const reposition = () => {
      // Cheap re-trigger of the layout effect via a state nudge.
      setTipStyle((s) => ({ ...s }))
    }
    window.addEventListener('scroll', reposition, true) // capture: catches inner scrollers
    window.addEventListener('resize', reposition)
    return () => {
      window.removeEventListener('scroll', reposition, true)
      window.removeEventListener('resize', reposition)
    }
  }, [open])

  return (
    <span class="ion-data-value-chip-wrap">
      <span
        ref={chipRef}
        class={kindInfo ? `ion-data-value-chip ion-data-value-chip--${kindInfo.cssKind}` : 'ion-data-value-chip'}
        role="figure"
        tabIndex={0}
        aria-describedby={open ? tooltipId : undefined}
        aria-label={ariaLabel}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        {/* Verified mark — a CSS-drawn check (::before on .ion-data-value-chip__icon)
            so it is byte-identical to the [[V:N]] streaming pre-fill in
            MarkdownRenderer. An SVG here vs a glyph in the pre-fill made the
            checkmark visibly change on hydration (the blink the user saw). */}
        {(!kindInfo || kindInfo.showCheck) && (
          <span class="ion-data-value-chip__icon" aria-hidden="true"></span>
        )}
        <span class="ion-data-value-chip__value">{formatted}</span>
        {metric.unit && (
          <span class="ion-data-value-chip__unit">{metric.unit}</span>
        )}
        {kindInfo && (
          <span class="ion-data-value-chip__kind" aria-hidden="true">{kindInfo.chipLabel}</span>
        )}
      </span>
      {open && typeof document !== 'undefined' &&
        createPortal(
          <span
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            class="ion-data-value-chip__tooltip"
            style={{
              position: 'fixed',
              top: `${tipStyle.top}px`,
              left: `${tipStyle.left}px`,
              visibility: tipStyle.visibility,
            }}
          >
            <span class="ion-data-value-chip__tooltip-cell">{cellLabel}</span>
            <span class="ion-data-value-chip__tooltip-metric">
              {metricLabel}
              {metric.condition && (
                <span class="ion-data-value-chip__tooltip-basis">
                  {' · '}
                  {metric.condition}
                </span>
              )}
              {basisHint && (
                <span class="ion-data-value-chip__tooltip-basis">
                  {' · '}
                  {basisHint}
                </span>
              )}
            </span>
            {/* Chemistry line intentionally removed (2026-06-05, TS):
                the tooltip is the PROVENANCE surface — which value, from
                which cell, via which chain — not a spec recap. */}
            {Array.isArray(provenanceTrail) && provenanceTrail.length > 0 && (
              <ol class="ion-data-value-chip__tooltip-trail">
                {provenanceTrail.map(renderTrailStep)}
              </ol>
            )}
            {rescueNote && (
              <span class="ion-data-value-chip__tooltip-rescue">{rescueNote}</span>
            )}
            {kindInfo && kindInfo.lines.length > 0 && (
              <ul class="ion-data-value-chip__tooltip-provenance">
                {kindInfo.lines.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            )}
            {kindInfo ? (
              <span class={`ion-data-value-chip__tooltip-source ion-data-value-chip__tooltip-source--${kindInfo.cssKind}`}>
                {kindInfo.title}
              </span>
            ) : (
              <span class="ion-data-value-chip__tooltip-source">
                Verified · Batemo catalogue
              </span>
            )}
          </span>,
          document.body,
        )}
    </span>
  )
}
